import React, { useState } from 'react';
import { Table, Spin, message, Button, Popconfirm, Space, Select, Input } from 'antd';
import { SaveOutlined, DeleteOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { NumberInput } from '../components/number_input';
import {
  useGetVessels,
  useUpdateVessel,
  useDeleteVessel,
  useGetVesselTypes,
  useCreateVesselType,
  useUpdateVesselType,
  useDeleteVesselType,
} from '~/services/vessel';
import { TVesselType } from '~/services/vessel/vessel.types';
import { TVessel } from '~/types/vessel';

// Google Calendar color options
const VESSEL_COLORS = [
  { value: 'tomato', label: 'Tomato', hex: '#ef4444' },
  { value: 'flamingo', label: 'Flamingo', hex: '#f472b6' },
  { value: 'tangerine', label: 'Tangerine', hex: '#f97316' },
  { value: 'banana', label: 'Banana', hex: '#facc15' },
  { value: 'sage', label: 'Sage', hex: '#4ade80' },
  { value: 'basil', label: 'Basil', hex: '#16a34a' },
  { value: 'peacock', label: 'Peacock', hex: '#3b82f6' },
  { value: 'blueberry', label: 'Blueberry', hex: '#2563eb' },
  { value: 'lavender', label: 'Lavender', hex: '#a78bfa' },
  { value: 'grape', label: 'Grape', hex: '#7c3aed' },
  { value: 'graphite', label: 'Graphite', hex: '#4b5563' },
];

const VehicleTypesSection: React.FC = () => {
  const { data: vesselTypesData, isLoading } = useGetVesselTypes();
  const createMutation = useCreateVesselType();
  const updateMutation = useUpdateVesselType();
  const deleteMutation = useDeleteVesselType();

  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const handleAdd = async () => {
    if (!newName.trim()) {
      message.warning('Please enter a vehicle type name');
      return;
    }
    try {
      await createMutation.mutateAsync({ name: newName.trim(), description: newDescription.trim() || undefined });
      message.success('Vehicle type added');
      setNewName('');
      setNewDescription('');
    } catch {
      message.error('Failed to add vehicle type');
    }
  };

  const handleUpdate = async (id: string) => {
    if (!editName.trim()) {
      message.warning('Name cannot be empty');
      return;
    }
    try {
      await updateMutation.mutateAsync({ id, name: editName.trim(), description: editDescription.trim() || undefined });
      message.success('Vehicle type updated');
      setEditingId(null);
    } catch {
      message.error('Failed to update vehicle type');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      message.success('Vehicle type deleted');
    } catch {
      message.error('Failed to delete vehicle type. It may be in use by existing vessels.');
    }
  };

  const startEditing = (vt: TVesselType) => {
    setEditingId(vt.id);
    setEditName(vt.name);
    setEditDescription(vt.description || '');
  };

  const vesselTypes = vesselTypesData?.items || [];

  const vtColumns: ColumnsType<TVesselType> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      render: (_, record) =>
        editingId === record.id ? (
          <Input value={editName} onChange={(e) => setEditName(e.target.value)} size="small" />
        ) : (
          record.name
        ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: '45%',
      render: (_, record) =>
        editingId === record.id ? (
          <Input value={editDescription} onChange={(e) => setEditDescription(e.target.value)} size="small" />
        ) : (
          record.description || '—'
        ),
    },
    {
      title: 'Vessels',
      key: 'count',
      width: '10%',
      render: (_, record) => record.vessels?.length || 0,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '15%',
      render: (_, record) => (
        <Space size="small">
          {editingId === record.id ? (
            <>
              <Button size="small" type="primary" icon={<SaveOutlined />} loading={updateMutation.isPending} onClick={() => handleUpdate(record.id)}>
                Save
              </Button>
              <Button size="small" onClick={() => setEditingId(null)}>Cancel</Button>
            </>
          ) : (
            <>
              <Button size="small" icon={<EditOutlined />} onClick={() => startEditing(record)}>Edit</Button>
              <Popconfirm
                title="Delete vehicle type"
                description={`Delete "${record.name}"? This will fail if vessels are using this type.`}
                onConfirm={() => handleDelete(record.id)}
                okText="Yes"
                cancelText="No"
                okType="danger"
              >
                <Button size="small" icon={<DeleteOutlined />} danger>Delete</Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <h3 className="text-lg font-medium mb-2">Vehicle Types</h3>
      <p className="text-gray-500 mb-4">
        Manage the vehicle types available when adding new vessels
      </p>

      <div className="flex gap-2 mb-4">
        <Input
          placeholder="Type name (e.g. Sprinter Van)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={handleAdd}
          style={{ maxWidth: 250 }}
        />
        <Input
          placeholder="Description (optional)"
          value={newDescription}
          onChange={(e) => setNewDescription(e.target.value)}
          onPressEnter={handleAdd}
          style={{ maxWidth: 300 }}
        />
        <Button type="primary" icon={<PlusOutlined />} loading={createMutation.isPending} onClick={handleAdd}>
          Add Type
        </Button>
      </div>

      <Table
        columns={vtColumns}
        dataSource={vesselTypes}
        rowKey="id"
        pagination={false}
        size="small"
        loading={isLoading}
      />
    </div>
  );
};

interface VesselsTabProps {
  // Keep the interface for backward compatibility but we'll use real data
  vessels?: any[];
  onVesselsChange?: (vessels: any[]) => void;
}

export const VesselsTab: React.FC<VesselsTabProps> = () => {
  const { data: vesselsData, isLoading, refetch } = useGetVessels();
  const updateVesselMutation = useUpdateVessel();
  const deleteVesselMutation = useDeleteVessel();
  const [loadingVesselId, setLoadingVesselId] = useState<string | null>(null);
  const [editedRates, setEditedRates] = useState<Record<string, number>>({});
  const [editedDirectCosts, setEditedDirectCosts] = useState<Record<string, number>>({});
  const [editedColors, setEditedColors] = useState<Record<string, string>>({});

  const handleRateInputChange = (id: string, value: number) => {
    setEditedRates(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleDirectCostInputChange = (id: string, value: number) => {
    setEditedDirectCosts(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleColorChange = (id: string, value: string) => {
    setEditedColors(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSave = async (vessel: TVessel) => {
    const newRate = editedRates[vessel.id];
    const newDirectCost = editedDirectCosts[vessel.id];
    const newColor = editedColors[vessel.id];

    const hasRateChange = newRate !== undefined && newRate !== vessel.hourlyRate;
    const hasDirectCostChange = newDirectCost !== undefined && newDirectCost !== vessel.directCost;
    const hasColorChange = newColor !== undefined && newColor !== vessel.color;

    if (!hasRateChange && !hasDirectCostChange && !hasColorChange) {
      message.info('No changes to save');
      return;
    }

    setLoadingVesselId(vessel.id);
    try {
      const updatePayload: any = { id: vessel.id };
      if (hasRateChange) updatePayload.hourlyRate = newRate;
      if (hasDirectCostChange) updatePayload.directCost = newDirectCost;
      if (hasColorChange) updatePayload.color = newColor;

      await updateVesselMutation.mutateAsync(updatePayload);
      message.success('Vessel updated successfully');

      setEditedRates(prev => {
        const updated = { ...prev };
        delete updated[vessel.id];
        return updated;
      });
      setEditedDirectCosts(prev => {
        const updated = { ...prev };
        delete updated[vessel.id];
        return updated;
      });
      setEditedColors(prev => {
        const updated = { ...prev };
        delete updated[vessel.id];
        return updated;
      });
      refetch();
    } catch (error) {
      message.error('Failed to update vessel');
      console.error('Error updating vessel:', error);
    } finally {
      setLoadingVesselId(null);
    }
  };

  const handleDelete = async (vessel: TVessel) => {
    try {
      await deleteVesselMutation.mutateAsync(vessel.id);
      message.success(`Vessel "${vessel.name}" deleted successfully`);
      refetch();
    } catch (error) {
      message.error('Failed to delete vessel');
      console.error('Error deleting vessel:', error);
    }
  };

  const getCurrentRate = (vessel: TVessel) => {
    return editedRates[vessel.id] !== undefined ? editedRates[vessel.id] : (vessel.hourlyRate || 0);
  };

  const getCurrentDirectCost = (vessel: TVessel) => {
    return editedDirectCosts[vessel.id] !== undefined ? editedDirectCosts[vessel.id] : (vessel.directCost || 0);
  };

  const getCurrentColor = (vessel: TVessel) => {
    return editedColors[vessel.id] !== undefined ? editedColors[vessel.id] : (vessel.color || '');
  };

  const hasChanges = (vessel: TVessel) => {
    const hasRateChange = editedRates[vessel.id] !== undefined && editedRates[vessel.id] !== vessel.hourlyRate;
    const hasDirectCostChange = editedDirectCosts[vessel.id] !== undefined && editedDirectCosts[vessel.id] !== vessel.directCost;
    const hasColorChange = editedColors[vessel.id] !== undefined && editedColors[vessel.id] !== vessel.color;
    return hasRateChange || hasDirectCostChange || hasColorChange;
  };

  const columns: ColumnsType<TVessel> = [
    {
      title: 'Vessel ID',
      dataIndex: 'vesselRef',
      key: 'vesselRef',
      width: '15%',
    },
    {
      title: 'Vessel Name',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
    },
    {
      title: 'Type',
      dataIndex: 'vesselType',
      key: 'vesselType',
      width: '15%',
      render: (vesselType) => vesselType?.name || 'N/A',
    },
    {
      title: 'Hourly Rate ($)',
      dataIndex: 'hourlyRate',
      key: 'hourlyRate',
      width: '15%',
      render: (_, record: TVessel) => (
        <NumberInput
          value={getCurrentRate(record)}
          prefix="$"
          loading={loadingVesselId === record.id}
          onChange={(newValue) => handleRateInputChange(record.id, newValue)}
        />
      ),
    },
    {
      title: 'Direct Cost ($)',
      dataIndex: 'directCost',
      key: 'directCost',
      width: '12%',
      render: (_, record: TVessel) => (
        <NumberInput
          value={getCurrentDirectCost(record)}
          prefix="$"
          loading={loadingVesselId === record.id}
          onChange={(newValue) => handleDirectCostInputChange(record.id, newValue)}
        />
      ),
    },
    {
      title: 'Color',
      dataIndex: 'color',
      key: 'color',
      width: '15%',
      render: (_, record: TVessel) => {
        const currentColor = getCurrentColor(record);
        const selectedColorObj = VESSEL_COLORS.find(c => c.value === currentColor);

        return (
          <Select
            style={{ width: '100%' }}
            value={currentColor || undefined}
            placeholder="Select color"
            onChange={(value) => handleColorChange(record.id, value)}
            disabled={loadingVesselId === record.id}
            options={VESSEL_COLORS.map(color => ({
              value: color.value,
              label: (
                <div className="flex items-center gap-2">
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      backgroundColor: color.hex,
                      border: '1px solid #d1d5db'
                    }}
                  />
                  <span>{color.label}</span>
                </div>
              )
            }))}
          />
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '13%',
      render: (_, record: TVessel) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<SaveOutlined />}
            loading={loadingVesselId === record.id}
            disabled={!hasChanges(record)}
            onClick={() => handleSave(record)}
          >
            Save
          </Button>
          <Popconfirm
            title="Delete vessel"
            description={`Are you sure you want to delete "${record.name}"?`}
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
            okType="danger"
          >
            <Button
              type="default"
              size="small"
              icon={<DeleteOutlined />}
              loading={deleteVesselMutation.isPending}
              danger
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium mb-2">Vessel Configuration</h3>
          <p className="text-gray-500 mb-4">
            Set hourly rates, direct costs, and calendar colors for each vessel in your fleet
          </p>
        </div>
        <div className="flex justify-center py-8">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <VehicleTypesSection />

      <div className="border-t pt-6">
        <h3 className="text-lg font-medium mb-2">Vessel Configuration</h3>
        <p className="text-gray-500 mb-4">
          Set hourly rates, direct costs, and calendar colors for each vessel in your fleet
        </p>
      </div>

      <Table
        columns={columns}
        dataSource={vesselsData?.items || []}
        rowKey="id"
        pagination={false}
        className="bg-white"
        loading={updateVesselMutation.isPending}
      />
    </div>
  );
};
